import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { useAuth } from "../App";
import { toast } from "sonner";
import {
  Plus, Search, Filter, FileText, Calendar, DollarSign, ChevronRight, Users, ArrowUpDown,
  Download, Upload, Loader2,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { format } from "date-fns";
import { CreateTransactionModal } from "../components/transactions/CreateTransactionModal";

const formatDateSafe = (dateStr, fmt = 'MMM d, yyyy') => {
  if (!dateStr) return '';
  const datePart = dateStr.split('T')[0];
  const [y, m, d] = datePart.split('-').map(Number);
  return format(new Date(y, m - 1, d), fmt);
};

const formatCurrency = (value) => {
  if (!value) return "-";
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(value);
};

const statusOrder = { active_listing: 0, active: 1, through_inspections: 2, pending: 2, closed: 3 };

const Transactions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [transactions, setTransactions] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const initialStatus = searchParams.get("status") || "open";
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [coordinatorFilter, setCoordinatorFilter] = useState("all");
  const [sortBy, setSortBy] = useState("closing_date");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const importRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [txnRes, wfRes, usersRes, contactsRes] = await Promise.all([
        axios.get(`${API}/transactions`, { withCredentials: true }),
        axios.get(`${API}/workflows`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/contacts`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setTransactions(txnRes.data);
      setWorkflows(wfRes.data);
      setActiveUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setContacts(Array.isArray(contactsRes.data) ? contactsRes.data : []);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    if (searchParams.get("action") === "new") {
      setIsModalOpen(true);
      searchParams.delete("action");
      setSearchParams(searchParams);
    }
  }, [fetchData, searchParams, setSearchParams]);

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/transactions/export-csv`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `transactions_export_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Transactions exported");
    } catch {
      toast.error("Failed to export transactions");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/transactions/import-csv`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data.message);
      if (res.data.errors?.length > 0) {
        res.data.errors.forEach((err) => toast.error(err));
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to import transactions");
    } finally {
      setImporting(false);
      if (importRef.current) importRef.current.value = "";
    }
  };

  const filteredTransactions = useMemo(() => transactions.filter(txn => {
    const matchesSearch = txn.property_address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all"
      ? true
      : statusFilter === "open"
        ? txn.status !== "closed" && txn.status !== "cancelled"
        : txn.status === statusFilter || (statusFilter === "through_inspections" && txn.status === "pending");
    const matchesCoordinator = coordinatorFilter === "all"
      ? true
      : coordinatorFilter === "unassigned"
        ? !txn.assigned_coordinator_id
        : txn.assigned_coordinator_id === coordinatorFilter;
    return matchesSearch && matchesStatus && matchesCoordinator;
  }).sort((a, b) => {
    if (sortBy === "closing_date") {
      const dateA = a.closing_date || "9999";
      const dateB = b.closing_date || "9999";
      return dateA.localeCompare(dateB);
    }
    return (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99);
  }),
  [transactions, searchTerm, statusFilter, coordinatorFilter, sortBy]);

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 animate-fade-in">
      {/* Header */}
      <div className="page-header -mx-6 md:-mx-8 -mt-6 md:-mt-8 mb-6 px-6 md:px-8 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[var(--t-text)] [font-family:var(--t-heading-font)] tracking-tight">Transactions</h1>
            <p className="text-[var(--t-text-muted)] mt-1">Manage your real estate transactions</p>
          </div>
          <div className="flex items-center gap-2">
            {isSuperAdmin && (
              <>
                <Button variant="outline" size="sm" onClick={handleExport} className="text-xs h-9" data-testid="export-csv-btn">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Export
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => importRef.current?.click()}
                  disabled={importing}
                  className="text-xs h-9"
                  data-testid="import-csv-btn"
                >
                  {importing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                  {importing ? "Importing..." : "Import"}
                </Button>
                <input ref={importRef} type="file" accept=".csv" onChange={handleImport} className="hidden" data-testid="import-csv-input" />
              </>
            )}
            <Button onClick={() => setIsModalOpen(true)} className="btn-primary" data-testid="new-transaction-btn">
              <Plus className="w-4 h-4 mr-2" />New Transaction
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--t-text-subtle)]" />
          <Input placeholder="Search by property address..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-transactions" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
            <Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open Transactions</SelectItem>
            <SelectItem value="all">All Transactions</SelectItem>
            <SelectItem value="active_listing">Active Listings</SelectItem>
            <SelectItem value="active">Before Inspections</SelectItem>
            <SelectItem value="through_inspections">Through Inspections</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-full sm:w-44" data-testid="sort-by">
            <ArrowUpDown className="w-4 h-4 mr-2" /><SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="closing_date">Close Date</SelectItem>
            <SelectItem value="status">Status</SelectItem>
          </SelectContent>
        </Select>
        <Select value={coordinatorFilter} onValueChange={setCoordinatorFilter}>
          <SelectTrigger className="w-full sm:w-52" data-testid="coordinator-filter">
            <Users className="w-4 h-4 mr-2" /><SelectValue placeholder="Filter by coordinator" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Coordinators</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {activeUsers.map((u) => (<SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {/* Transactions List */}
      {filteredTransactions.length > 0 ? (
        <div className="space-y-4" data-testid="transactions-list">
          {filteredTransactions.map((txn, index) => {
            const rowIdx = index % 8 + 1;
            const rowStyle = { background: `var(--t-row-${rowIdx})`, borderColor: `var(--t-row-${rowIdx}-border)` };
            return (
              <Link key={txn.transaction_id} to={`/transactions/${txn.transaction_id}`} data-testid={`transaction-item-${index}`}>
                <Card className="border hover:shadow-md transition-all cursor-pointer" style={{ ...rowStyle, borderRadius: 'var(--t-card-radius)' }}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1">
                        <div className="p-3 bg-[var(--t-text)]/8 rounded-xl">
                          <FileText className="w-6 h-6 text-[var(--t-text)]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-semibold text-[var(--t-text)] truncate">{txn.property_address}</h3>
                          <div className="flex flex-wrap items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-xs capitalize">{txn.property_type.replace('_', ' ')}</Badge>
                            <Badge variant="outline" className="text-xs capitalize">Rep: {txn.transaction_side} Side</Badge>
                            <Badge className={`status-badge ${txn.status}`}>
                              {txn.status === 'active_listing' ? 'Active Listing' : txn.status === 'active' ? 'Before Inspections' : (txn.status === 'through_inspections' || txn.status === 'pending') ? 'Through Inspections' : txn.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="text-right hidden sm:block">
                        <div className="flex items-center gap-2 text-sm text-[var(--t-text-muted)] mb-1">
                          <Calendar className="w-4 h-4" /><span>Closing: {formatDateSafe(txn.closing_date)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-[var(--t-text-muted)]">
                          <DollarSign className="w-4 h-4" /><span>{formatCurrency(txn.purchase_price)}</span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-[var(--t-text-subtle)] flex-shrink-0" />
                    </div>
                    {txn.tasks?.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-[var(--t-border-light)]">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-[var(--t-text-muted)]">
                            {txn.tasks.filter(t => t.status === 'completed').length} of {txn.tasks.length} tasks completed
                          </span>
                          <span className="text-[var(--t-text)] font-medium">
                            {Math.round((txn.tasks.filter(t => t.status === 'completed').length / txn.tasks.length) * 100)}%
                          </span>
                        </div>
                        <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-[var(--t-primary)] rounded-full transition-all" style={{ width: `${(txn.tasks.filter(t => t.status === 'completed').length / txn.tasks.length) * 100}%` }} />
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      ) : (
        <Card className="card-surface">
          <CardContent className="py-12">
            <div className="empty-state">
              <FileText className="empty-state-icon" />
              <p className="empty-state-title">No transactions found</p>
              <p className="empty-state-description">
                {searchTerm || statusFilter !== "open" ? "Try adjusting your search or filters" : "Get started by creating your first transaction"}
              </p>
              {!searchTerm && statusFilter === "open" && (
                <Button onClick={() => setIsModalOpen(true)} className="btn-primary mt-4">
                  <Plus className="w-4 h-4 mr-2" />New Transaction
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <CreateTransactionModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        workflows={workflows}
        contacts={contacts}
        onCreated={fetchData}
      />
    </div>
  );
};

export default Transactions;
